import React, { useEffect, useState, useMemo } from 'react';
import SearchContext from '../../state/context';
import {
  buildQuerySummary,
  buildSearchBox,
  buildFacet,
  buildPager,
  buildSort,
  buildRelevanceSortCriterion,
  buildDateSortCriterion,
  SortOrder,
  buildResultList,
  buildFieldSortCriterion,
  loadQueryActions,
  loadAdvancedSearchQueryActions
} from '@coveo/headless';
import QuerySummary from './QuerySummary';
import SearchBox from '../../modules/LocationSelector/SearchBox';
import { bindUrlManager } from '../../urlManager';
import { Facet } from './Facet';
import { Pager } from '../Pager';
import { Sort } from '../Sort';
import { ListingRowResults } from '../ListingRowResults';
import useOutsideClick from '../../../hooks/useClickOutside';
import DropdownFacet from '../DropdownFacet';

const ListingRow = props => {
  const {
    coveoExpression = null,
    coveoSortCaptions = '',
    coveoSortOptions = '',
    coveoEngine,
    coveoContextLanguage = 'en',
    coveoSource = 'Production US - English',
    applyFiltersLabel = 'Apply Filters',
    clearSearchLabelMobile = 'Clear Search',
    showSearchBox = true,
    clearResultsLabel = 'Clear Results',
    singleSelectFields,
    singleSelectTitles,
    multiSelectFields,
    multiSelectTitles,
    filterResultsLabel = 'Filter Results',
    sortByLabel = 'Sort By',
    locationCookieName,
    appliedLabel = 'applied',
    defaultSortOption = ''
  } = props;

  const [hideFilters, setHideFilters] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [hasFacetSelected, setHasFacetSelected] = useState(false);
  const [selectedFacetsPerField, setSelectedFacetsPerField] = useState({});
  const singleSelectFacetFields =
    singleSelectFields.length > 0 ? singleSelectFields.split('|') : [];
  const multiSelectFacetFields =
    multiSelectFields.length > 0 ? multiSelectFields.split('|') : [];
  const singleSelectFacetTitles = singleSelectTitles.split('|');
  const multiSelectFacetTitles = multiSelectTitles.split('|');
  const sortOptions = coveoSortOptions.split('|');
  const sortCaptions = coveoSortCaptions.split('|');

  const fieldsToInclude = useMemo(() => ['ssl_heading', 'excerpt', 'ssl_modified', 'ssl_resourcedate'], []);
  if (singleSelectFields.length > 0)
    fieldsToInclude.push(...singleSelectFields.split('|'));
  if (multiSelectFields.length > 0)
    fieldsToInclude.push(...multiSelectFields.split('|'));

  const singleSelectFacets = singleSelectFacetFields.map(field => {
    const options = { facetId: field, field: field };
    return {
      title: singleSelectFacetTitles[singleSelectFacetFields.indexOf(field)],
      isSingleSelect: true,
      controller: buildFacet(coveoEngine, { options })
    };
  });

  const multiSelectFacets = multiSelectFacetFields.map(field => {
    const options = {
      facetId: field,
      field: field
    };
    return {
      title: multiSelectFacetTitles[multiSelectFacetFields.indexOf(field)],
      isSingleSelect: false,
      controller: buildFacet(coveoEngine, { options })
    };
  });

  const facets = [...singleSelectFacets, ...multiSelectFacets];

  let criteria = [];
  if (sortOptions.length > 0) {
    criteria = sortOptions.map((option, index) => {
      const splitOption = option.split(' ');
      if (splitOption.length === 1) {
        switch (splitOption[0]) {
          case 'relevancy':
            return [sortCaptions[index], buildRelevanceSortCriterion()];
          default:
            return [sortCaptions[index], buildRelevanceSortCriterion()];
        }
      } else {
        switch (splitOption[0]) {
          case 'date':
            return [
              sortCaptions[index],
              buildDateSortCriterion(
                splitOption[1] === 'ascending'
                  ? SortOrder.Ascending
                  : SortOrder.Descending
              )
            ];
          default:
            return [
              sortCaptions[index],
              buildFieldSortCriterion(
                splitOption[0],
                splitOption[1] === 'ascending'
                  ? SortOrder.Ascending
                  : SortOrder.Descending
              )
            ];
        }
      }
    });
  }

  let defaultSortIndex = 2;
  switch(defaultSortOption) {
    case 'Date Descending':
      defaultSortIndex = 1;
      break;
    case 'Date Ascending':
      defaultSortIndex = 0;
      break;
    case 'A-Z':
      defaultSortIndex = 3;
      break;
    case 'Z-A':
      defaultSortIndex = 4;
      break;
    default:
      defaultSortIndex = 2;
      break;
  }

  const initialCriterion = criteria[defaultSortIndex][1];
  const querySummary = useMemo(() => buildQuerySummary(coveoEngine), []);
  const searchBox = useMemo(() => buildSearchBox(coveoEngine), []);
  const pager = useMemo(() => buildPager(coveoEngine), []);
  const unsubscribeUrlManager = useMemo(() => bindUrlManager(coveoEngine), []);

  const sort = useMemo(() =>
    buildSort(
      coveoEngine,
      {
        initialState: { criterion: initialCriterion }
      },
      []
    )
  );
  const resultsListController = useMemo(() =>
    buildResultList(
      coveoEngine,
      {
        options: {
          fieldsToInclude: fieldsToInclude
        }
      },
      []
    )
  );

  useEffect(() => {
    resultsListController.subscribe(() => {
      if (resultsListController.state.isLoading) return;
      setHasFacetSelected(
        facets.some(facet => facet.controller.state.hasActiveValues)
      );
    });

    let commonQuery = null;
    let advancedQuery = null;
    if (coveoExpression) {
      commonQuery = { cq: coveoExpression };
    }
    if (coveoContextLanguage && coveoSource) {
      advancedQuery = {
        aq: `(@source="${coveoSource}") (@ssl_contextlanguage="${coveoContextLanguage}")`
      };
    }

    //join commonQuery and advancedQuery in one object
    let joinedAdvancedQuery = null;
    if (commonQuery || advancedQuery) {
      joinedAdvancedQuery = { ...commonQuery, ...advancedQuery };
    }

    const advancedQuerySearchActionCreator = loadAdvancedSearchQueryActions(coveoEngine);
    const { updateQuery } = loadQueryActions(coveoEngine);
    const data = localStorage.getItem('coveo_standalone_search_box_data');

    if (joinedAdvancedQuery) {
      coveoEngine.dispatch(advancedQuerySearchActionCreator.registerAdvancedSearchQueries(joinedAdvancedQuery));
    }

    if (data) {
      const { value, analytics } = JSON.parse(data);
      localStorage.removeItem('coveo_standalone_search_box_data');
      coveoEngine.dispatch(updateQuery({ q: value }));
      coveoEngine.executeFirstSearchAfterStandaloneSearchBoxRedirect(analytics);
    } else {
      coveoEngine.executeFirstSearch();
    }

    return () => {
      unsubscribeUrlManager();
    };
  }, []);

  const onClearAllFacets = () => {
    facets.forEach(facet => {
      facet.controller.deselectAll();
    });
  };

  const onClearHash = () => {
    window.location.hash = '';
  };

  const onResultFacetClick = (field, tagValue) => {
    facets.forEach(facet => {
      facet.controller.state.values.forEach(value => {
        if (Object.values(value).includes(tagValue)) {
          facet.controller.toggleSingleSelect(value);
        }
      });
    });
  };

  const handleApplyFilters = () => {
    const hash = window.location.hash;
    const hashArray = hash.split('&');
    const newHashArray = hashArray.filter(item => !item.includes('f'));
    const newHash = newHashArray.join('&');

    const selectedFacets = [];
    Object.keys(selectedFacetsPerField).forEach(field => {
      const facets = selectedFacetsPerField[field];
      facets.forEach(facet => {
        selectedFacets.push(`f-${field}=${facet.value}`);
      });
    });

    const newHashWithFacets = `${newHash}${
      newHash.length > 0 ? '&' : ''
    }${selectedFacets.join('&')}`;
    window.location.hash = newHashWithFacets;
  };

  const handleFacetChange = (facet, selectedValues) => {
    const facetId = facet.controller.state.facetId;
    const newSelectedFacetsPerField = { ...selectedFacetsPerField };
    newSelectedFacetsPerField[facetId] = selectedValues;
    setSelectedFacetsPerField(newSelectedFacetsPerField);
  };

  const ref = useOutsideClick(() => {
    setSearchActive(false);
  });

  return (
    <SearchContext.Provider
      value={{
        engine: coveoEngine,
        resultsListController: resultsListController
      }}
    >
      <div className="listing-row container">
        <div className="listing-row__facets-and-results-container">
          <div className="listing-row__facets-column">
            <div className="listing-row__facets-header">
              {filterResultsLabel}
              <button
                className="listing-row__facet-column-header-clear-btn"
                onClick={onClearHash}
                disabled={!hasFacetSelected}
              >
                {clearResultsLabel}
              </button>
            </div>
            {facets.map((facet, index) => (
              <Facet key={index} controller={facet.controller} facet={facet} />
            ))}
          </div>
          <div className="listing-row__results-column">
            <div className="listing-row__coveo-results-header">
              <div className="listing-row__summary-section">
                <QuerySummary controller={querySummary} />
              </div>
              <div className="listing-row__mobile-filters-hide-show-section">
                <span>
                  <button
                    className="link listing-row__facet-column-header-clear-btn listing-row__hide-show-filters-btn"
                    type="button"
                    onClick={() => setHideFilters(!hideFilters)}
                  >
                    <span className="link__label">
                      {hideFilters ? 'Show Filters' : 'Hide Filters'}
                    </span>
                  </button>
                </span>
                <span className="search-list-row__mobile-filters-info">
                  {facets.reduce((acc, facet) => {
                    return (
                      acc +
                      facet.controller.state.values.filter(
                        value => value.state === 'selected'
                      ).length
                    );
                  }, 0)}{' '}
                  {appliedLabel}
                </span>
              </div>
              <div
                className={`listing-row__mobile-filters-section  ${
                  hideFilters ? 'hidden' : ''
                }`}
              >
                {facets.map((facet, index) => (
                  <DropdownFacet
                    key={index}
                    controller={facet.controller}
                    facet={facet}
                    onFacetChange={handleFacetChange}
                  />
                ))}
              </div>
              <div className="listing-row__mobile-apply-filters-section">
                <button className="btn btn--submit listing-row__apply-filters-button" type="submit" onClick={() => handleApplyFilters()}>
                  <span className="btn__label">{applyFiltersLabel}</span>
                  <svg width="11" height="18" role="presentation">
                    <use xlinkHref="#arrow-right"></use>
                  </svg>
                </button>
                <span>
                  <button className="link listing-row__facet-column-header-clear-btn" type="button" onClick={() => onClearAllFacets()}>
                    <span className="link__label">
                      {clearSearchLabelMobile}
                    </span>
                  </button>
                </span>
              </div>
              <div className="listing-row__coveo-sort-and-search">
                <div className={`listing-row__sort-dropdown ${searchActive ? 'search-active' : ''}`}>
                  {sortByLabel}&nbsp;
                  <Sort controller={sort} criteria={criteria} />
                  <svg viewBox="0 0 18 11" width="18" height="11" role="presentation">
                    <use xlinkHref="#arrow-down"></use>
                  </svg>
                </div>
                {showSearchBox && (
                  <div
                    ref={ref}
                    className={`listing-row__coveo-search-bar ${
                      searchActive ? 'search-active' : ''
                    }`}
                  >
                    <div className="listing-row__search-box">
                      <button
                        className="listing-row__coveo-search-btn"
                        aria-label="Search"
                        onClick={() => setSearchActive(!searchActive)}
                      >
                        <svg
                          viewBox="0 0 31 33"
                          height="30"
                          role="presentation"
                        >
                          <use xlinkHref="#search"></use>
                        </svg>
                      </button>
                      <div>
                        <SearchBox
                          placeholder={'Type to Search...'}
                          controller={searchBox}
                          locationCookieName={locationCookieName}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <ListingRowResults
              controller={resultsListController}
              facetFields={[
                ...singleSelectFacetFields,
                ...multiSelectFacetFields
              ]}
              facets={facets}
              onFacetClick={onResultFacetClick}
            />
          </div>
        </div>
        <div className="listing-row__results-footer">
          <Pager controller={pager} className={'listing-row__pager'} />
        </div>
      </div>
    </SearchContext.Provider>
  );
};

export default ListingRow;
